/**
 * @file filters.ts
 * @description Pixel-level image processing filters.
 */

import { EventBus } from '@hcie/shared';
import { layers, g } from './globals';
import { DrawAction, historyManager } from './history';

/**
 * Common Color Correction Filters
 */
export const ColorFilters = {
    /**
     * Brightness & Contrast
     */
    applyBrightnessContrast(brightness: number, contrast: number): void {
        this.processPixels('Brightness/Contrast', (data) => {
            const b = brightness;
            const c = (contrast + 100) / 100; // 0..2
            
            for (let i = 0; i < data.length; i += 4) {
                // Contrast
                data[i] = (data[i] - 128) * c + 128;
                data[i + 1] = (data[i + 1] - 128) * c + 128;
                data[i + 2] = (data[i + 2] - 128) * c + 128;
                
                // Brightness
                data[i] += b;
                data[i + 1] += b;
                data[i + 2] += b;
            }
        });
    },

    /**
     * Hue & Saturation
     */
    applyHueSaturation(hue: number, saturation: number): void {
        this.processPixels('Hue/Saturation', (data) => {
            const h = hue / 360;
            const s = (saturation + 100) / 100;

            for (let i = 0; i < data.length; i += 4) {
                let [rh, rs, rv] = this.rgbToHsv(data[i], data[i+1], data[i+2]);
                
                rh = (rh + h) % 1;
                rs = Math.max(0, Math.min(1, rs * s));
                
                const [r, gr, bl] = this.hsvToRgb(rh, rs, rv);
                data[i] = r;
                data[i + 1] = gr;
                data[i + 2] = bl;
            }
        });
    },

    /**
     * Helper: Process current layer pixels with history support
     */
    processPixels(name: string, transformer: (data: Uint8ClampedArray) => void): void {
        const activeIdx = g.activeLayerIndex;
        const layer = layers[activeIdx];
        if (!layer || !layer.ctx) return;

        const w = g.image_width;
        const h = g.image_height;
        const ctx = layer.ctx as CanvasRenderingContext2D;
        
        const oldData = ctx.getImageData(0, 0, w, h);
        const newData = ctx.getImageData(0, 0, w, h);
        
        transformer(newData.data);
        
        ctx.putImageData(newData, 0, 0);
        
        historyManager.push(new DrawAction(activeIdx, oldData, newData));
        EventBus.emit('RENDER_LAYERS');
        console.log(`[Filter] Applied ${name}`);
    },

    // Conversion Utilities
    rgbToHsv(r: number, g: number, b: number): [number, number, number] {
        r /= 255; g /= 255; b /= 255;
        const max = Math.max(r, g, b), min = Math.min(r, g, b);
        let h = 0, s = 0, v = max;
        const d = max - min;
        s = max === 0 ? 0 : d / max;
        if (max !== min) {
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break;
                case g: h = (b - r) / d + 2; break;
                case b: h = (r - g) / d + 4; break;
            }
            h /= 6;
        }
        return [h, s, v];
    },

    hsvToRgb(h: number, s: number, v: number): [number, number, number] {
        let r = 0, g = 0, b = 0;
        const i = Math.floor(h * 6);
        const f = h * 6 - i;
        const p = v * (1 - s);
        const q = v * (1 - f * s);
        const t = v * (1 - (1 - f) * s);
        switch (i % 6) {
            case 0: r = v; g = t; b = p; break;
            case 1: r = q; g = v; b = p; break;
            case 2: r = p; g = v; b = t; break;
            case 3: r = p; g = q; b = v; break;
            case 4: r = t; g = p; b = v; break;
            case 5: r = v; g = p; b = q; break;
        }
        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }
};
